import { useState, useContext, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack,
  ContentCopy,
  CheckCircle,
  Cancel,
  Delete,
  Save,
  Restaurant,
  AttachMoney,
  CalendarMonth,
  WarningAmber,
} from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { UserContext } from '../../components/shared/useUser';
import { RecipeV2Tab, RecipeStatus, RecipeV2Detail, RecipeKind } from './types';
import { applyRoundAndFormat } from '../../utils';
import useRecipeV2Detail from './hooks/useRecipeV2Detail';
import useRecipeV2Api from './hooks/useRecipeV2Api';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import IngredientsTab from './components/IngredientsTab';
import MethodTab from './components/MethodTab';
import CostingTab from './components/CostingTab';
import AllergensAndDietaryTab from './components/AllergensAndDietaryTab';
import CarbonTab from './components/CarbonTab';
import VersionsTab from './components/VersionsTab';
import AIInsightsTab from './components/AIInsightsTab';
import ProcessChecksTab from './components/ProcessChecksTab';
import UsageTab from './components/UsageTab';
import TrialTab from './components/TrialTab';
import CarbonRatingChip from './components/CarbonRatingChip';
import AllergenChips from '../../components/shared/AllergenChips';
import DataCompletenessChip from './components/DataCompletenessChip';
import { isDemoModeEnabled } from '../../utils/featureAccess';
import NutritionalDataTab from '../../components/shared/nutrition/NutritionalDataTab';
import { NutritionSource, ProductNutritionalInfo, emptyNutritionalInfo } from '../../components/shared/nutrition/types';
import { getMockRecipeNutrition } from './utils/mockNutrition';

const statusColor: Record<RecipeStatus, 'success' | 'warning' | 'info' | 'default' | 'error'> = {
  [RecipeStatus.Live]: 'success',
  [RecipeStatus.Draft]: 'default',
  [RecipeStatus.ReadyForReview]: 'warning',
  [RecipeStatus.Approved]: 'info',
  [RecipeStatus.Superseded]: 'default',
  [RecipeStatus.Archived]: 'default',
  [RecipeStatus.Blocked]: 'error',
};

const RecipeV2DetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('recipeV2');
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermissionTo } = useContext(UserContext);
  const [tabValue, setTabValue] = useState<RecipeV2Tab>(RecipeV2Tab.Ingredients);
  const { recipe, isLoading, error, loadRecipe, setRecipe } = useRecipeV2Detail();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const api = useRecipeV2Api();
  const isDemoMode = isDemoModeEnabled();

  // Approval dialog state
  const [approvalDialog, setApprovalDialog] = useState<'approve' | 'reject' | null>(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [approvalSaving, setApprovalSaving] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Save (update metadata) state
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Demo Mode only - recipe-derived nutritional info, see RecipeV2Tab.Nutrition
  const [nutritionalInfo, setNutritionalInfo] = useState<ProductNutritionalInfo>(emptyNutritionalInfo);

  const recipeId = Number(id);

  /** Applies an edit to a top-level recipe field (yield, dates, production method, etc.)
   * made from the Ingredients/Method tabs' meta bars, and marks the page dirty for Save. */
  const handleMetadataChange = (partial: Partial<RecipeV2Detail>) => {
    setRecipe((prev) => (prev ? { ...prev, ...partial } : prev));
    setIsDirty(true);
  };

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  useEffect(() => {
    if (!isNaN(recipeId)) {
      loadRecipe(recipeId);
      setNutritionalInfo(emptyNutritionalInfo);
    }
  }, [recipeId, loadRecipe]);

  // Demo Mode only - once the recipe has loaded, seed the Nutritional Info tab with
  // deterministic recipe-derived values instead of leaving it empty (see mockNutrition.ts).
  // Guarded by a ref (not just `recipe`) so later metadata edits (which also call setRecipe)
  // don't clobber nutrition values the user has since edited by hand.
  const nutritionSeededForRecipeId = useRef<number | null>(null);
  useEffect(() => {
    if (isDemoModeEnabled() && recipe && nutritionSeededForRecipeId.current !== recipe.id) {
      nutritionSeededForRecipeId.current = recipe.id;
      setNutritionalInfo(getMockRecipeNutrition(recipe));
    }
  }, [recipe]);

  const handleClone = async () => {
    if (!recipe) return;
    try {
      const cloned = await api.cloneRecipe(recipe.id);
      navigate(`/store/recipes-v2/${cloned.id}`);
    } catch { /* noop */ }
  };

  const handleApproveReject = async () => {
    if (!recipe || !approvalDialog) return;
    setApprovalSaving(true);
    try {
      if (approvalDialog === 'approve') {
        await api.approveRecipe(recipe.id, { comment: approvalComment });
      } else {
        await api.rejectRecipe(recipe.id, { comment: approvalComment });
      }
      setApprovalDialog(null);
      setApprovalComment('');
      loadRecipe(recipeId);
    } catch { /* noop */ }
    setApprovalSaving(false);
  };

  const handleDelete = async () => {
    if (!recipe) return;
    setIsDeleting(true);
    try {
      await api.deleteRecipe(recipe.id);
      navigate('/store/recipes-v2');
    } catch { /* noop */ }
    setIsDeleting(false);
    setDeleteDialogOpen(false);
  };

  const handleSave = async () => {
    if (!recipe) return;
    setIsSaving(true);
    try {
      await api.updateRecipe(recipe.id, {
        description: recipe.description,
        primaryCategory: recipe.primaryCategory,
        yieldQuantity: recipe.yieldQuantity,
        yieldUom: recipe.yieldUom,
        effectiveFrom: recipe.effectiveFrom,
        effectiveTo: recipe.effectiveTo,
        productionMethodId: recipe.productionMethodId,
      });
      setIsDirty(false);
      loadRecipe(recipeId);
    } catch { /* noop */ }
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <PageArea>
        <Stack alignItems="center"
          justifyContent="center"
          sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      </PageArea>
    );
  }

  if (error || !recipe) {
    return (
      <PageArea>
        <Stack spacing={2}>
          <Button startIcon={<ArrowBack />}
            onClick={() => navigate('/store/recipes-v2')}>
            {t('actions.back')}
          </Button>
          <Typography color="error">{error || t('notFound')}</Typography>
        </Stack>
      </PageArea>
    );
  }

  const hasWritePermission = hasPermissionTo(['RecipeV2Write']);
  const hasDeletePermission = hasPermissionTo(['RecipeV2Delete']);
  const hasApprovePermission = hasPermissionTo(['RecipeV2Approve']);
  const hasCostingPermission = hasPermissionTo(['RecipeV2CostingRead']);
  const hasAIPermission = hasPermissionTo(['RecipeV2AIRead']);
  const hasAllergenPermission = hasPermissionTo(['RecipeV2Read']);
  const hasCarbonPermission = hasPermissionTo(['RecipeV2Read']);
  const hasVersionPermission = hasPermissionTo(['RecipeV2Read']);
  const hasProcessChecksPermission = hasPermissionTo(['RecipeV2Read']);
  const hasUsagePermission = hasPermissionTo(['RecipeV2Read']);
  const hasExportPermission = hasPermissionTo(['RecipeV2Export']);

  const showApprovalActions = hasApprovePermission &&
    (recipe.status === RecipeStatus.ReadyForReview || recipe.status === RecipeStatus.Draft);

  return (
    <PageArea>
      {/* Header: title/subtitle + status chips (left), action buttons (top right) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        <Stack direction="row"
          alignItems="flex-start"
          spacing={1}>
          <IconButton onClick={() => navigate('/store/recipes-v2')}
            size="small"
            sx={{ mt: 0.5 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <PageTitle subTitle={`${recipe.itemNumber} · ${recipe.primaryCategory}`}>
              {recipe.customerFacingName ?? recipe.description}
            </PageTitle>
            {recipe.operationalName && recipe.operationalName !== (recipe.customerFacingName ?? recipe.description) && (
              <Typography variant="textSM"
                color="text.secondary"
                sx={{ mt: 0.5 }}>
                {t('kitchenName', 'Kitchen name: {{name}}', { name: recipe.operationalName })}
              </Typography>
            )}
            <Stack direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1 }}>
              <Chip
                label={t(`status.${recipe.status.charAt(0).toLowerCase() + recipe.status.slice(1)}`)}
                color={statusColor[recipe.status] ?? 'default'}
                size="small"
              />
              {recipe.recipeType === RecipeKind.Batch && (
                <Chip label={t('batch.badge')}
                  size="small"
                  color="secondary"
                  variant="outlined" />
              )}
              {recipe.carbonScore && (
                <CarbonRatingChip rating={recipe.carbonScore} />
              )}
              <Chip label={`v${recipe.currentVersion}`}
                size="small"
                variant="outlined" />
              {recipe.aiIssueCount > 0 && (
                <Chip
                  label={t('ai.issueCount', { count: recipe.aiIssueCount })}
                  size="small"
                  color="warning"
                />
              )}
              <DataCompletenessChip completeness={recipe.dataCompleteness} />
            </Stack>
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          {showApprovalActions && (
            <>
              <Button size="md"
                startIcon={<CheckCircle />}
                variant="primary"
                onClick={() => setApprovalDialog('approve')}>
                {t('actions.approve')}
              </Button>
              <Button size="md"
                startIcon={<Cancel />}
                variant="secondary"
                color="error"
                onClick={() => setApprovalDialog('reject')}>
                {t('actions.reject')}
              </Button>
            </>
          )}
          {hasWritePermission && (
            <Button size="md"
              startIcon={<ContentCopy />}
              variant="secondary"
              onClick={handleClone}>
              {t('actions.clone')}
            </Button>
          )}
          {hasWritePermission && isDirty && (
            <Button size="md"
              startIcon={<Save />}
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}>
              {t('actions.save')}
            </Button>
          )}
          {hasDeletePermission && (
            <Button size="md"
              startIcon={<Delete />}
              variant="secondary"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}>
              {t('actions.delete')}
            </Button>
          )}
        </Stack>
      </Box>

      {recipe.dataCompleteness === 'summary' && (
        <Alert severity="info"
          sx={{ mt: 3 }}>
          {t(
            'incompleteData.banner',
            'A full recipe record for {{name}} has not been written up yet - it only exists in the British Kitchen catalogue summary. The commercial, allergen, method and sustainability data shown below are sensible estimates based on its category and price point, not chef-authored figures. Replace this with a full recipe as soon as one is developed.',
            { name: recipe.customerFacingName ?? recipe.description },
          )}
        </Alert>
      )}

      {recipe.story && (
        <Typography variant="textSM"
          color="text.secondary"
          sx={{ mt: 3, maxWidth: 900, fontStyle: 'italic' }}>
          {recipe.story}
        </Typography>
      )}

      {recipe.suitableFor && recipe.suitableFor.length > 0 && (
        <Stack direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 2 }}>
          {recipe.suitableFor.map((occasion) => (
            <Chip key={occasion}
              label={occasion}
              size="small"
              variant="outlined" />
          ))}
        </Stack>
      )}

      {/* Dashboard tiles: headline detail at a glance */}
      <Box
        sx={{
          mt: 4,
          mb: 4,
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: 'repeat(5, 1fr)',
          },
        }}
      >
        <Section style={{ p: 5, mb: 0 }}>
          <Box display="flex"
            alignItems="center"
            justifyContent="space-between">
            <Box flex={1}>
              <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                {t('yield')}
              </Box>
              <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                {recipe.yieldQuantity} {recipe.yieldUom}
              </Box>
              <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                {recipe.primaryCategory}
              </Box>
            </Box>
            <Box sx={{ backgroundColor: '#1976d220', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1976d2' }}>
              <SvgIcon fontSize="medium">
                <Restaurant />
              </SvgIcon>
            </Box>
          </Box>
        </Section>

        <Section style={{ p: 5, mb: 0 }}>
          <Box display="flex"
            alignItems="center"
            justifyContent="space-between">
            <Box flex={1}>
              <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                {t('cost')}
              </Box>
              <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                {applyRoundAndFormat(recipe.totalCost, currencyCode)}
              </Box>
              <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                {t('build.production')}
              </Box>
            </Box>
            <Box sx={{ backgroundColor: '#2e7d3220', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2e7d32' }}>
              <SvgIcon fontSize="medium">
                <AttachMoney />
              </SvgIcon>
            </Box>
          </Box>
        </Section>

        <Section style={{ p: 5, mb: 0 }}>
          <Box display="flex"
            alignItems="center"
            justifyContent="space-between">
            <Box flex={1}>
              <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                {t('costPerPortion')}
              </Box>
              <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                {applyRoundAndFormat(recipe.costPerPortion, currencyCode)}
              </Box>
              <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                {t('costing.perPortion', 'Per portion')}
              </Box>
            </Box>
            <Box sx={{ backgroundColor: '#ed6c0220', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ed6c02' }}>
              <SvgIcon fontSize="medium">
                <AttachMoney />
              </SvgIcon>
            </Box>
          </Box>
        </Section>

        <Section style={{ p: 5, mb: 0 }}>
          <Box display="flex"
            alignItems="center"
            justifyContent="space-between">
            <Box flex={1}>
              <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                {t('dateRange')}
              </Box>
              <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                {recipe.effectiveFrom}
              </Box>
              <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                {recipe.effectiveTo ? `→ ${recipe.effectiveTo}` : `→ ${t('build.noEndDate')}`}
              </Box>
            </Box>
            <Box sx={{ backgroundColor: '#6a1b9a20', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6a1b9a' }}>
              <SvgIcon fontSize="medium">
                <CalendarMonth />
              </SvgIcon>
            </Box>
          </Box>
        </Section>

        <Section style={{ p: 5, mb: 0 }}>
          <Box display="flex"
            alignItems="center"
            justifyContent="space-between">
            <Box flex={1}>
              <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                {t('allergensLabel')}
              </Box>
              <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                {recipe.allergenSummary.length}
              </Box>
              {recipe.allergenSummary.length > 0 ? (
                <AllergenChips allergens={recipe.allergenSummary}
                  max={3} />
              ) : (
                <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                  {t('allergens.noAllergens')}
                </Box>
              )}
            </Box>
            <Box sx={{ backgroundColor: '#d32f2f20', borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d32f2f' }}>
              <SvgIcon fontSize="medium">
                <WarningAmber />
              </SvgIcon>
            </Box>
          </Box>
        </Section>
      </Box>

      {/* Tabs */}
      <Tabs value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: '-1px',
          ml: 4,
          mr: 4,
          px: 2,
        }}>
        <Tab label={t('tabs.ingredients')}
          value={RecipeV2Tab.Ingredients} />
        <Tab label={t('tabs.method')}
          value={RecipeV2Tab.Method} />
        {hasCostingPermission && <Tab label={t('tabs.costing')}
          value={RecipeV2Tab.Costing} />}
        {hasAllergenPermission && <Tab label={t('tabs.allergensAndDietary', 'Allergens & Dietary')}
          value={RecipeV2Tab.AllergensDietary} />}
        {isDemoMode && <Tab label={t('tabs.nutrition', 'Nutritional Info')}
          value={RecipeV2Tab.Nutrition} />}
        {hasCarbonPermission && <Tab label={t('tabs.carbon')}
          value={RecipeV2Tab.Carbon} />}
        {isDemoMode && <Tab label={t('tabs.trial', 'Trial')}
          value={RecipeV2Tab.Trial} />}
        {isDemoMode && hasProcessChecksPermission && <Tab label={t('tabs.processChecks', 'Process Checks')}
          value={RecipeV2Tab.ProcessChecks} />}
        {isDemoMode && hasUsagePermission && <Tab label={t('tabs.usage', 'Usage')}
          value={RecipeV2Tab.Usage} />}
        {hasAIPermission && (
          <Tab
            label={
              <Stack direction="row"
                spacing={0.5}
                alignItems="center">
                <span>{t('tabs.aiInsights')}</span>
                {recipe.aiFindings.length > 0 && (
                  <Chip label={recipe.aiFindings.length}
                    size="small"
                    color="warning"
                    sx={{ height: 18, fontSize: 11 }} />
                )}
              </Stack>
            }
            value={RecipeV2Tab.AIInsights}
          />
        )}
        {hasVersionPermission && <Tab label={t('tabs.versions')}
          value={RecipeV2Tab.Versions} />}
      </Tabs>

      {/* Tabs ride a Section housing the split of data for the active tab.
          Padding matches Procurement Products (Section p:5/pb:0 + inner Box p:{xs:4,md:5}). */}
      <Section style={{ mt: 0, p: 5, pb: 0 }}>
        <Box sx={{ p: { xs: 4, md: 5 } }}>
          {tabValue === RecipeV2Tab.Ingredients && <IngredientsTab recipe={recipe}
            editable={hasWritePermission}
            currencyCode={currencyCode}
            onDirtyChange={setIsDirty}
            onMetadataChange={handleMetadataChange} />}
          {tabValue === RecipeV2Tab.Method && <MethodTab recipe={recipe}
            editable={hasWritePermission}
            onMetadataChange={handleMetadataChange} />}
          {tabValue === RecipeV2Tab.Costing && <CostingTab recipe={recipe}
            currencyCode={currencyCode} />}
          {tabValue === RecipeV2Tab.AllergensDietary && <AllergensAndDietaryTab recipe={recipe} />}
          {isDemoMode && tabValue === RecipeV2Tab.Nutrition && (
            <NutritionalDataTab
              nutritionalInfo={nutritionalInfo}
              onChange={setNutritionalInfo}
              disabled={!hasWritePermission}
              source={NutritionSource.Recipe}
            />
          )}
          {tabValue === RecipeV2Tab.Carbon && <CarbonTab recipe={recipe} />}
          {isDemoMode && tabValue === RecipeV2Tab.Trial && (
            <TrialTab recipe={recipe}
              currencyCode={currencyCode} />
          )}
          {isDemoMode && tabValue === RecipeV2Tab.ProcessChecks && <ProcessChecksTab recipe={recipe} />}
          {isDemoMode && tabValue === RecipeV2Tab.Usage && <UsageTab recipe={recipe} />}
          {tabValue === RecipeV2Tab.AIInsights && <AIInsightsTab recipe={recipe} />}
          {tabValue === RecipeV2Tab.Versions && <VersionsTab recipe={recipe} />}
        </Box>
      </Section>

      {/* Approve/Reject Dialog */}
      <Dialog open={approvalDialog !== null}
        onClose={() => setApprovalDialog(null)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>
          {approvalDialog === 'approve' ? t('approval.approveTitle') : t('approval.rejectTitle')}
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={3}
            fullWidth
            label={t('approval.comment')}
            placeholder={t('approval.commentPlaceholder')}
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            sx={{ mt: 1 }}
            error={approvalDialog === 'reject' && approvalComment.trim() === ''}
            helperText={approvalDialog === 'reject' && approvalComment.trim() === '' ? t('approval.commentRequired') : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setApprovalDialog(null); setApprovalComment(''); }}
            variant="secondary">
            {t('approval.cancel')}
          </Button>
          <Button
            onClick={handleApproveReject}
            variant="primary"
            color={approvalDialog === 'reject' ? 'error' : 'primary'}
            disabled={approvalSaving || (approvalDialog === 'reject' && approvalComment.trim() === '')}
          >
            {approvalDialog === 'approve' ? t('approval.approveConfirm') : t('approval.rejectConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>{t('actions.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('actions.deleteConfirmMessage', { name: recipe.description })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}
            variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={handleDelete}
            variant="primary"
            color="error"
            disabled={isDeleting}
          >
            {t('actions.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default RecipeV2DetailPage;
